import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(token ? 'form' : 'error'); // form, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setStatus('success');
      toast.success('Mot de passe réinitialisé !');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
        if (detail.includes('expiré') || detail.includes('invalide')) {
          setStatus('error');
        }
      } else {
        toast.error('Une erreur est survenue');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_fad4c604-2efd-4142-a35f-93aeb2be7f74/artifacts/hzrybxar_logo2.jpg" alt="Phoenix Card Game" className="w-12 h-12 rounded-xl object-cover" />
          </Link>
          <h1 className="text-3xl font-bold text-white font-outfit">
            {status === 'success' ? 'Mot de passe modifié' : status === 'error' ? 'Lien invalide' : 'Nouveau mot de passe'}
          </h1>
        </div>

        <div className="bg-[#121212] rounded-2xl p-8 border border-white/10">
          {status === 'success' && (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-gray-300 mb-6">Votre mot de passe a été réinitialisé avec succès.</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white px-8 py-5"
                data-testid="go-to-login-btn"
              >
                Se connecter
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-4" data-testid="reset-error">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-gray-300 mb-2">Ce lien de réinitialisation est invalide ou a expiré.</p>
              <p className="text-gray-500 text-sm mb-6">Veuillez faire une nouvelle demande.</p>
              <Link to="/forgot-password">
                <Button className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white px-8 py-5" data-testid="new-reset-request-btn">
                  Nouvelle demande
                </Button>
              </Link>
            </div>
          )}

          {status === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-gray-300">Nouveau mot de passe</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 caractères"
                    required
                    minLength={6}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="reset-password-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="confirm" className="text-gray-300">Confirmer le mot de passe</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmez votre mot de passe"
                    required
                    minLength={6}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="reset-confirm-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white py-5"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
